import { useState, useEffect, useRef } from 'react';

import { Clock, X } from 'lucide-react';

import { Button } from '@/components/TailwindComponents/base/Button/Button';
import { Input } from '@/components/TailwindComponents/base/Input/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/TailwindComponents/base/Popover/Popover';
import { ScrollArea } from '@/components/TailwindComponents/base/ScrollArea/ScrollArea';
import { cn } from '@/components/TailwindComponents/lib/utils';

interface TimePickerProps {
  time?: string;
  onSelect?: (time: string | undefined) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  /**
   * @description If true, shows a clear (X) button when a time is selected.
   */
  clearable?: boolean;
}

export function TimePicker({
  time,
  onSelect,
  className,
  disabled,
  placeholder = 'HH:mm',
  clearable = false,
}: TimePickerProps) {
  const [selectedTime, setSelectedTime] = useState<string | undefined>(time);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<number | undefined>(
    time ? parseInt(time.split(':')[0]) : undefined,
  );
  const [selectedMinute, setSelectedMinute] = useState<number | undefined>(
    time ? parseInt(time.split(':')[1]) : undefined,
  );
  const [inputValue, setInputValue] = useState(time || '');
  const hourScrollRef = useRef<HTMLDivElement>(null);
  const minuteScrollRef = useRef<HTMLDivElement>(null);

  // Sync internal state when external time prop changes
  useEffect(() => {
    setSelectedTime(time);
    setInputValue(time || '');
    if (time) {
      const [hour, minute] = time.split(':');
      setSelectedHour(parseInt(hour));
      setSelectedMinute(parseInt(minute));
    } else {
      setSelectedHour(undefined);
      setSelectedMinute(undefined);
    }
  }, [time]);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const handleHourSelect = (hour: number) => {
    setSelectedHour(hour);
    if (selectedMinute !== undefined) {
      const timeString = `${hour.toString().padStart(2, '0')}:${selectedMinute
        .toString()
        .padStart(2, '0')}`;
      setSelectedTime(timeString);
      setInputValue(timeString);
      onSelect?.(timeString);
    }
  };

  const handleMinuteSelect = (minute: number) => {
    setSelectedMinute(minute);
    if (selectedHour !== undefined) {
      const timeString = `${selectedHour.toString().padStart(2, '0')}:${minute
        .toString()
        .padStart(2, '0')}`;
      setSelectedTime(timeString);
      setInputValue(timeString);
      onSelect?.(timeString);
      setIsOpen(false); // Close popover when minute is selected
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);

    const match = val.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      const h = parseInt(match[1]);
      const m = parseInt(match[2]);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        const timeString = `${h.toString().padStart(2, '0')}:${m
          .toString()
          .padStart(2, '0')}`;
        setSelectedHour(h);
        setSelectedMinute(m);
        setSelectedTime(timeString);
        onSelect?.(timeString);
      }
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setSelectedTime(undefined);
    setInputValue('');
    setSelectedHour(undefined);
    setSelectedMinute(undefined);
    onSelect?.(undefined);
    setIsOpen(false);
  };

  const handleClearFromTrigger = (
    e: React.MouseEvent | React.KeyboardEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedTime(undefined);
    setSelectedHour(undefined);
    setSelectedMinute(undefined);
    onSelect?.(undefined);
  };

  const showClearButton = clearable && selectedTime && !disabled;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-between text-left font-normal border-input text-foreground',
            !selectedTime && 'text-muted-foreground',
            className,
          )}
          disabled={disabled}
        >
          <span className="flex-1 truncate text-left">
            {selectedTime ? selectedTime : <span>{placeholder}</span>}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {showClearButton && (
              <span
                className="flex items-center cursor-pointer hover:text-red-500 transition-colors"
                onClick={handleClearFromTrigger}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                tabIndex={0}
                role="button"
                aria-label="Clear time"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleClearFromTrigger(e);
                  }
                }}
              >
                <X className="w-4 h-4" />
              </span>
            )}
            <Clock className="h-4 w-4" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <div className="flex flex-col">
          <div className="p-3 border-b">
            <Input
              type="text"
              placeholder={placeholder}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              className="text-center text-base"
              maxLength={5}
            />
          </div>
          <div className="flex">
            <ScrollArea
              className="w-80 sm:w-auto h-[240px]"
              ref={hourScrollRef}
            >
              <div className="flex flex-col p-3">
                {hours.map((hour) => (
                  <Button
                    key={hour}
                    size="default"
                    variant={selectedHour === hour ? 'default' : 'ghost'}
                    className="w-full justify-start mb-2 text-base"
                    onClick={() => handleHourSelect(hour)}
                  >
                    {hour.toString().padStart(2, '0')}
                  </Button>
                ))}
              </div>
            </ScrollArea>
            <ScrollArea
              className="w-72 sm:w-auto h-[240px]"
              ref={minuteScrollRef}
            >
              <div className="flex flex-col p-3">
                {minutes.map((minute) => (
                  <Button
                    key={minute}
                    size="default"
                    variant={selectedMinute === minute ? 'default' : 'ghost'}
                    className="w-full justify-start mb-2 text-base"
                    onClick={() => handleMinuteSelect(minute)}
                  >
                    {minute.toString().padStart(2, '0')}
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </div>
          <div className="p-3 border-t flex gap-2">
            <Button
              variant="ghost"
              size="default"
              className="flex-1"
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              size="default"
              className="flex-1"
              onClick={() => setIsOpen(false)}
            >
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
